import apiClient from '@/lib/api/client';
import type {
  NgoSettingsResponse,
  NgoSettingsPatchPayload,
  SupportingClubsResponse,
  PledgesResponse,
  PledgePaymentStatus,
  PledgeItem,
  PledgeReceiptData,
  BrowseNgosResponse,
  NgosListResponse,
  NgoDetail,
  NgoStatus,
  NgoStatusUpdatePayload,
  NgoBankingPayload,
  NgoBankingResponse,
  NgoBankingDetailsResponse,
  NgoDashboardStatsResponse,
  NgoDonationHistoryPoint,
} from '@/types/ngo.types';

export const ngoService = {
  async getAdminNgos(params?: {
    search?: string;
    page?: number;
    limit?: number;
    status?: NgoStatus;
  }): Promise<NgosListResponse> {
    const response = await apiClient.get<NgosListResponse>('/ngos', { params });
    return response.data;
  },

  async getAdminNgoById(id: string): Promise<NgoDetail> {
    const response = await apiClient.get<NgoDetail>(`/ngos/${id}`);
    return response.data;
  },

  async updateAdminNgoStatus(id: string, payload: NgoStatusUpdatePayload): Promise<void> {
    await apiClient.patch(`/ngos/${id}/status`, payload);
  },

  async getSettings(): Promise<NgoSettingsResponse> {
    const response = await apiClient.get<NgoSettingsResponse>('/settings/ngo');
    return response.data;
  },

  async updateSettings(payload: NgoSettingsPatchPayload): Promise<NgoSettingsResponse> {
    const response = await apiClient.patch<NgoSettingsResponse>('/settings/ngo', payload);
    return response.data;
  },

  /** GET /api/v1/ngos/me/banking — Fetch logged-in NGO's saved banking details */
  async getBanking(): Promise<NgoBankingDetailsResponse> {
    const response = await apiClient.get<NgoBankingDetailsResponse>('/ngos/me/banking');
    return response.data;
  },

  /** PATCH /api/v1/ngos/me/banking — Update logged-in NGO's banking details */
  async updateBanking(payload: NgoBankingPayload): Promise<NgoBankingResponse> {
    const response = await apiClient.patch<NgoBankingResponse>('/ngos/me/banking', payload);
    return response.data;
  },

  async getSupportingClubs(params: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<SupportingClubsResponse> {
    const response = await apiClient.get<SupportingClubsResponse>('/ngos/supporting-clubs', {
      params,
    });
    return response.data;
  },

  async getPledges(params: {
    search?: string;
    page?: number;
    limit?: number;
    paymentStatus?: PledgePaymentStatus;
  }): Promise<PledgesResponse> {
    const response = await apiClient.get<PledgesResponse>('/pledges', { params });
    return response.data;
  },

  async getPledgeById(id: string): Promise<PledgeItem> {
    const response = await apiClient.get<PledgeItem>(`/pledges/${id}`);
    return response.data;
  },

  async downloadReceipt(id: string): Promise<PledgeReceiptData> {
    const response = await apiClient.get<{ receipt: PledgeReceiptData }>(`/pledges/${id}/receipt`);
    return response.data.receipt;
  },

  async sendReminder(id: string): Promise<void> {
    await apiClient.post(`/pledges/${id}/remind`);
  },

  /** POST /api/v1/pledges/:id/confirm-payment — NGO marks club's direct payment as received */
  async confirmPledgePayment(id: string): Promise<{ message?: string }> {
    const response = await apiClient.post<{ message?: string }>(`/pledges/${id}/confirm-payment`);
    return response.data;
  },

  async getDashboardStats(): Promise<NgoDashboardStatsResponse> {
    const response = await apiClient.get<NgoDashboardStatsResponse>('/ngos/me/dashboard-stats');
    return response.data;
  },

  async getDonationHistory(clubId?: string): Promise<NgoDonationHistoryPoint[]> {
    const response = await apiClient.get<NgoDonationHistoryPoint[]>('/ngos/me/donation-history', {
      params: clubId ? { clubId } : undefined,
    });
    return response.data;
  },

  async browseNgos(params: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<BrowseNgosResponse> {
    const response = await apiClient.get<BrowseNgosResponse>('/ngos/browse', { params });
    return response.data;
  },

  async addToWishlist(ngoId: string): Promise<void> {
    await apiClient.post(`/ngos/${ngoId}/wishlist`);
  },

  async removeFromWishlist(ngoId: string): Promise<void> {
    await apiClient.delete(`/ngos/${ngoId}/wishlist`);
  },
};
